/**
 * Business Hours utilities.
 *
 * Display provider's business hours and current open/closed status.
 * Helps users know when to expect a response.
 *
 * Config stored in user.publicData.businessHours = {
 *   timezone: 'Europe/Helsinki',
 *   schedule: {
 *     monday:    { open: '09:00', close: '17:00' },
 *     tuesday:   { open: '09:00', close: '17:00' },
 *     ...
 *     sunday:    null   — closed on Sunday
 *   }
 * }
 */

export const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

/**
 * Get business hours from user.
 * @param {Object|null} user
 * @returns {Object|null}
 */
export const getBusinessHours = (user) =>
  user?.attributes?.profile?.publicData?.businessHours || null;

/**
 * Parse HH:MM to minutes since midnight.
 * @param {string} time — "HH:MM"
 * @returns {number}
 */
export const parseTime = (time) => {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return -1;
  const [h, m] = time.split(':').map(Number);
  if (h > 23 || m > 59) return -1;
  return h * 60 + m;
};

/**
 * Check if provider is currently open.
 * @param {Object} businessHours — { timezone, schedule }
 * @param {Date} [now]
 * @returns {boolean}
 */
export const isCurrentlyOpen = (businessHours, now = new Date()) => {
  if (!businessHours?.schedule) return false;

  // Get current time in provider's timezone
  const tz = businessHours.timezone || 'UTC';
  const localTime = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false, weekday: 'long',
  }).formatToParts(now);

  const dayPart = localTime.find(p => p.type === 'weekday')?.value?.toLowerCase() || '';
  const hourPart = localTime.find(p => p.type === 'hour')?.value || '00';
  const minPart = localTime.find(p => p.type === 'minute')?.value || '00';
  const currentMinutes = Number(hourPart) * 60 + Number(minPart);

  const todaySchedule = businessHours.schedule[dayPart];
  if (!todaySchedule) return false; // closed today

  const openMinutes = parseTime(todaySchedule.open);
  const closeMinutes = parseTime(todaySchedule.close);

  return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
};

/**
 * Get today's schedule.
 * @param {Object} businessHours
 * @param {Date} [now]
 * @returns {Object|null}
 */
export const getTodaySchedule = (businessHours, now = new Date()) => {
  if (!businessHours?.schedule) return null;
  const dayName = DAYS_OF_WEEK[now.getDay()];
  return businessHours.schedule[dayName] || null;
};

/**
 * Validate business hours config.
 * @param {Object} config
 * @returns {string|null}
 */
export const validateBusinessHours = (config) => {
  if (!config) return null; // no hours set = always available
  if (!config.schedule) return 'Schedule required';
  for (const [day, hours] of Object.entries(config.schedule)) {
    if (!DAYS_OF_WEEK.includes(day)) return `Invalid day: ${day}`;
    if (hours !== null && hours !== undefined) {
      if (!hours.open || !hours.close) return `Day "${day}" missing open or close time`;
      if (parseTime(hours.open) < 0) return `Invalid open time for "${day}"`;
      if (parseTime(hours.close) < 0) return `Invalid close time for "${day}"`;
      if (parseTime(hours.open) >= parseTime(hours.close)) return `Open must be before close for "${day}"`;
    }
  }
  return null;
};
